import { useEffect, useMemo, useState, type MutableRefObject } from "react";
import { InfoScreenCrossfade } from "@/pages/info-screen/components/InfoScreenCrossfade";
import { InfoScreenEventsModule } from "@/pages/info-screen/components/InfoScreenEventsModule";
import { InfoScreenRotatingDisplay } from "@/pages/info-screen/components/InfoScreenRotatingDisplay";
import { InfoScreenState } from "@/pages/info-screen/components/InfoScreenState";
import { infoScreenModuleRotationSeconds } from "@/pages/info-screen/infoScreenModuleRotation";
import type { InfoScreenContent } from "@/types/infoScreen";
import type { InfoScreenEventsPayload, InfoScreenModule } from "@/types/infoScreenEvents";

type Props = {
  content: InfoScreenContent;
  modules: InfoScreenModule[];
  eventsPayload: InfoScreenEventsPayload | null;
  imageIndexByPageRef?: MutableRefObject<Record<string, number>>;
};

const DEFAULT_MODULES: InfoScreenModule[] = [
  { id: "pages", type: "pages", enabled: true, sort_order: 10 },
  { id: "local_events", type: "local_events", enabled: true, sort_order: 25 },
];

export function InfoScreenModuleRotator({
  content,
  modules,
  eventsPayload,
  imageIndexByPageRef,
}: Props) {
  const activeModules = useMemo(() => {
    const list = (modules.length ? modules : DEFAULT_MODULES)
      .filter((m) => m.enabled)
      .sort((a, b) => a.sort_order - b.sort_order);

    return list.filter((m) => {
      if (m.type === "pages") {
        return content.enabled && content.pages.some((p) => p.enabled);
      }
      if (m.type === "local_events") {
        return eventsPayload?.enabled !== false;
      }
      return false;
    });
  }, [modules, content, eventsPayload]);

  const [moduleIndex, setModuleIndex] = useState(0);

  const rotationSeconds = useMemo(() => {
    const mod = activeModules[moduleIndex];
    if (!mod) {
      return Math.max(5, content.page_rotation_seconds);
    }
    return infoScreenModuleRotationSeconds(mod, content, eventsPayload);
  }, [activeModules, moduleIndex, content, eventsPayload]);

  useEffect(() => {
    setModuleIndex(0);
  }, [content.revision, eventsPayload?.events.length]);

  useEffect(() => {
    if (activeModules.length <= 1) return;
    const ms = Math.max(5, rotationSeconds) * 1000;
    const timer = window.setInterval(() => {
      setModuleIndex((i) => (i + 1) % activeModules.length);
    }, ms);
    return () => window.clearInterval(timer);
  }, [activeModules.length, rotationSeconds]);

  useEffect(() => {
    if (moduleIndex >= activeModules.length) setModuleIndex(0);
  }, [moduleIndex, activeModules.length]);

  if (activeModules.length === 0) {
    return (
      <InfoScreenState
        title="Keine Inhalte aktiv"
        subtitle="Bitte aktivieren Sie Seiten oder Veranstaltungen in den Stammdaten."
      />
    );
  }

  const current = activeModules[moduleIndex] ?? activeModules[0];
  if (!current) {
    return null;
  }

  const moduleKey = `${current.id}-${moduleIndex}`;

  if (current.type === "local_events") {
    return (
      <div className="info-screen-module-rotator info-screen-module-view info-screen-module-view--events">
        <InfoScreenCrossfade itemKey={moduleKey} variant="fade">
          <InfoScreenEventsModule
            cityLabel={eventsPayload?.city_label ?? "Frankfurt am Main"}
            events={eventsPayload?.events ?? []}
            rotationSeconds={eventsPayload?.rotation_seconds ?? 10}
          />
        </InfoScreenCrossfade>
      </div>
    );
  }

  return (
    <div className="info-screen-module-rotator info-screen-module-view info-screen-module-view--pages">
      <InfoScreenCrossfade itemKey={moduleKey} variant="fade">
        <InfoScreenRotatingDisplay
          content={content}
          imageIndexByPageRef={imageIndexByPageRef}
        />
      </InfoScreenCrossfade>
    </div>
  );
}
